import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PDFPage } from '@/types/floorPlan';

interface PDFPageSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  pages: PDFPage[];
  onPageSelect: (pageNumber: number) => void;
  onPageNameChange: (pageNumber: number, name: string) => void;
  onContinue: () => void;
  isLoading: boolean;
  progress: number;
  fileName: string;
  singlePageSelection?: boolean;
}

export function PDFPageSelectionModal({
  isOpen,
  onClose,
  pages,
  onPageSelect,
  onPageNameChange,
  onContinue,
  isLoading,
  progress,
  fileName,
  singlePageSelection = false,
}: PDFPageSelectionModalProps) {
  const selectedCount = pages.filter(p => p.selected).length;
  const [errors, setErrors] = React.useState<Record<number, string>>({});

  // Reference for the continue button to trigger via Enter key
  const continueButtonRef = React.useRef<HTMLButtonElement>(null);

  // Handle Enter key to submit
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && isOpen && selectedCount > 0 && !isLoading) {
        // Don't trigger if focus is on an input field (to allow normal input behavior)
        const target = e.target as HTMLElement;
        if (target.tagName !== 'INPUT') {
          e.preventDefault();
          continueButtonRef.current?.click();
        }
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, selectedCount, isLoading]);

  // Handle page selection based on mode
  const handlePageSelect = (pageNumber: number) => {
    if (singlePageSelection) {
      // In single page mode, deselect all other pages
      pages.forEach(page => {
        if (page.pageNumber !== pageNumber && page.selected) {
          onPageSelect(page.pageNumber); // This will toggle the selection off
        }
      });
    }

    // Toggle selection for the clicked page
    onPageSelect(pageNumber);
  };

  const validateNames = () => {
    const newErrors: Record<number, string> = {};
    let hasErrors = false;

    pages.forEach(page => {
      if (page.selected && !page.name.trim()) {
        newErrors[page.pageNumber] = 'Name is required';
        hasErrors = true;
      }
    });

    setErrors(newErrors);
    return !hasErrors;
  };

  const handleContinue = () => {
    if (validateNames()) {
      onContinue();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-6">
        <DialogHeader>
          <DialogTitle>
            {singlePageSelection ? 'Select Replacement Page' : 'Select and Name Pages'}
          </DialogTitle>
          <DialogDescription>
            {singlePageSelection
              ? 'Choose the page you want to use as a replacement for the current floor plan.'
              : 'Choose the pages you want to convert and provide names for each selected page.'}
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="space-y-2">
            <Progress value={progress} className="w-full" />
            <p className="text-sm text-muted-foreground">
              {progress < 100 ? 'Loading PDF pages...' : 'Processing selected pages...'}
            </p>
          </div>
        )}

        <ScrollArea className="h-[60vh] w-full">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 p-4">
            {pages.map(page => (
              <Card
                key={page.pageNumber}
                className={`relative overflow-hidden transition-all duration-200 hover:shadow-lg ${
                  page.selected ? 'ring-2 ring-primary ring-offset-2' : ''
                }`}
              >
                <div className="p-4 space-y-4">
                  <div className="relative">
                    <div
                      className="relative aspect-[1/1.414] h-40 cursor-pointer bg-muted rounded-md overflow-hidden"
                      onClick={() => handlePageSelect(page.pageNumber)}
                    >
                      <img
                        src={page.thumbnail}
                        alt={`Page ${page.pageNumber}`}
                        className="w-full h-full object-contain bg-white"
                      />
                      <div className="absolute top-2 right-2 z-10">
                        <Checkbox
                          checked={page.selected}
                          onCheckedChange={() => handlePageSelect(page.pageNumber)}
                          className="h-5 w-5 bg-white/90 backdrop-blur-sm data-[state=checked]:bg-primary"
                          onClick={e => e.stopPropagation()}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`page-${page.pageNumber}-name`} className="text-sm font-medium">
                      Page Name
                    </Label>
                    <Input
                      id={`page-${page.pageNumber}-name`}
                      value={page.name}
                      onChange={e => onPageNameChange(page.pageNumber, e.target.value)}
                      placeholder={`${fileName} - Page ${page.pageNumber}`}
                      disabled={!page.selected}
                      className={errors[page.pageNumber] ? 'border-red-500' : ''}
                    />
                    {errors[page.pageNumber] && (
                      <p className="text-sm text-red-500">{errors[page.pageNumber]}</p>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </ScrollArea>

        <div className="flex justify-between items-center mt-6 pt-4 border-t">
          <p className="text-sm text-muted-foreground">
            {selectedCount} page{selectedCount !== 1 ? 's' : ''} selected
          </p>
          <div className="space-x-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              ref={continueButtonRef}
              onClick={handleContinue}
              disabled={selectedCount === 0 || isLoading}
              className="bg-primary hover:bg-primary/90"
            >
              {singlePageSelection ? 'Use Selected Page' : 'Convert Selected Pages'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
